/**
 * @file: LeadHeaderCard.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The lead-detail hero. It anchors the opportunity in two protagonists: the
 * license-plate token (the screen's signature — the thing a service advisor
 * scans for) and the next-service deadline with an at-a-glance urgency read.
 * The vehicle and the who/what meta sit quietly beneath. Pure presentation.
 */

import { VehicleBrandLogo } from '@euler/app/components/VehicleBrandLogo';
import { Typography } from '@euler/app/components/typography';
import { leadStatusTone } from '@euler/app/flows/leads/pages/list/components/lead-card';
import { StatusPill } from '@euler/components/StatusPill';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { WithExtraVehicleModelProps } from '@euler/model/WithExtraVehicleModelProps';
import {
  Lead,
  LeadIntentionLabel,
  LeadStatusLabel,
  LeadTypeLabel,
  LeadVisibilityLabel,
} from '@euler/model/lead/Lead';
import { LeadItem } from '@euler/model/lead/LeadItem';
import { dateFromValue } from '@euler/utils/datetime';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import format from 'date-fns/format';
import {
  CircleDotDashed,
  Eye,
  EyeClosed,
  Pencil,
  Phone,
} from 'lucide-react-native';
import { memo } from 'react';
import { Linking, TouchableOpacity, View } from 'react-native';
import {
  Field,
  LD,
  cardShadowStyle,
  formatDate,
  intentionTone,
} from './leadDetailKit';

// The last-reach timestamp as MM-dd HH:mm, or '' when unreadable.
const formatReachTime = (value: string | Date): string => {
  try {
    return format(dateFromValue(value), 'MM-dd HH:mm');
  } catch {
    return '';
  }
};

// Calendar days until the target service date, or null when unreadable.
const daysUntil = (value: string | Date | undefined | null): number | null => {
  if (!value) return null;
  try {
    return differenceInCalendarDays(dateFromValue(value), new Date());
  } catch {
    return null;
  }
};

export const LeadHeaderCard = memo(
  ({
    lead,
    items,
    terminal,
    lastReach,
    closing,
    onEditCustomer,
    onEditIntention,
    onEditOwner,
    onEditVisibility,
    onEditTargetDate,
    onOpenReservation,
  }: {
    lead: WithExtraVehicleModelProps<Lead>;
    items: LeadItem[];
    terminal: boolean;
    lastReach?: { summary: string; at: string | Date } | null;
    /** How a closed lead closed; shown in place of the last outreach. */
    closing?: { summary: string; at: string | Date } | null;
    onEditCustomer?: () => void;
    onEditIntention?: () => void;
    onEditOwner?: () => void;
    onEditVisibility?: () => void;
    onEditTargetDate?: () => void;
    // When the lead is reserved, opens its reservation from the status pill.
    onOpenReservation?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();

    // Open eye = public, dashed ring = restricted, closed eye = private.
    const VisibilityIcon =
      lead.visibility === 'public'
        ? Eye
        : lead.visibility === 'private'
        ? EyeClosed
        : CircleDotDashed;

    // A single custom item names its own due line; otherwise it's 下次保养.
    const dueLabel =
      items.length === 1 && items[0].customLabel
        ? items[0].customLabel
        : '下次保养';

    // Urgency is only meaningful while the lead is still in play.
    const days = terminal ? null : daysUntil(lead.targetServiceDate);
    let urgency: { text: string; fg: string; bg: string } | null = null;
    if (days != null) {
      const tone =
        days < 0
          ? { fg: LD.danger, bg: LD.dangerSoft }
          : days <= 7
          ? { fg: LD.amber, bg: LD.amberSoft }
          : { fg: LD.steel, bg: LD.hairline };
      const text =
        days < 0
          ? `已逾期 ${-days} 天`
          : days === 0
          ? '今天到期'
          : `还有 ${days} 天`;
      urgency = { text, ...tone };
    }

    const customer = [lead.customerName, lead.customerPhone]
      .filter(Boolean)
      .join(' · ');
    const intention = intentionTone(lead.intention);

    return (
      <View
        style={{
          backgroundColor: LD.surface,
          borderRadius: rpx(14),
          paddingTop: rpx(16),
          paddingHorizontal: rpx(16),
          paddingBottom: rpx(6),
          marginBottom: rpx(10),
          ...cardShadowStyle(rpx),
        }}
      >
        {/* brand logo anchors the identity block on the left; plate + model
            and the full vehicle name stack to its right, status top-right */}
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <View
            style={{
              width: rpx(52),
              height: rpx(52),
              borderRadius: rpx(15),
              backgroundColor: LD.brandSoft,
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: rpx(12),
            }}
          >
            {lead.vehicleBrandLogo ? (
              <VehicleBrandLogo url={lead.vehicleBrandLogo} height={rpx(30)} />
            ) : (
              <Typography.Text size={rpx(20)} color={LD.brand} bold>
                {lead.licensePlateNo?.[0] ?? '车'}
              </Typography.Text>
            )}
          </View>
          <View style={{ flex: 1 }}>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  flex: 1,
                  paddingRight: rpx(8),
                }}
              >
                <Typography.Text
                  size={rpx(22)}
                  color={LD.ink}
                  bold
                  letterSpacing={rpx(1)}
                >
                  {lead.licensePlateNo || '未知车牌'}
                </Typography.Text>
                <Typography.Text
                  size={rpx(13)}
                  color={LD.steel}
                  medium
                  numberOfLines={1}
                  style={{ flex: 1, marginLeft: rpx(10) }}
                >
                  {lead.vehicleModel || ''}
                </Typography.Text>
              </View>
              <StatusPill
                label={LeadStatusLabel[lead.status]}
                tone={leadStatusTone(lead.status)}
                onPress={onOpenReservation}
              />
            </View>

            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginTop: rpx(4),
              }}
            >
              <Typography.Text size={rpx(13)} color={LD.steel}>
                {LeadTypeLabel[lead.type]} · 当前负责人:{' '}
              </Typography.Text>
              <TouchableOpacity
                activeOpacity={onEditOwner ? 0.6 : 1}
                disabled={!onEditOwner}
                onPress={onEditOwner}
                hitSlop={{ top: rpx(8), bottom: rpx(8), right: rpx(8) }}
                style={{ flexDirection: 'row', alignItems: 'center' }}
              >
                <Typography.Text
                  size={rpx(13)}
                  color={onEditOwner ? LD.brand : LD.steel}
                  medium
                >
                  {lead.assigneeName || '—'}
                </Typography.Text>
                {onEditOwner ? (
                  <Pencil
                    size={rpx(12)}
                    color={LD.mist}
                    style={{ marginLeft: rpx(4) }}
                  />
                ) : null}
              </TouchableOpacity>
              <Typography.Text size={rpx(13)} color={LD.steel}>
                {' · '}
              </Typography.Text>
              {/* visibility: an icon + label chip; tap to change. */}
              <TouchableOpacity
                activeOpacity={onEditVisibility ? 0.6 : 1}
                disabled={!onEditVisibility}
                onPress={onEditVisibility}
                hitSlop={{ top: rpx(8), bottom: rpx(8) }}
                style={{ flexDirection: 'row', alignItems: 'center' }}
              >
                <VisibilityIcon size={rpx(12)} color={LD.steel} />
                <Typography.Text
                  size={rpx(12)}
                  color={LD.steel}
                  medium
                  style={{ marginLeft: rpx(4) }}
                >
                  {LeadVisibilityLabel[lead.visibility]}
                </Typography.Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        <View
          style={{
            height: 1,
            backgroundColor: LD.hairline,
            marginTop: rpx(14),
            marginBottom: rpx(12),
          }}
        />

        {/* protagonist deadline, with the dial button trailing the whole block */}
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <View style={{ flex: 1 }}>
            {/* tap the label to reschedule the target service date */}
            <TouchableOpacity
              activeOpacity={onEditTargetDate ? 0.6 : 1}
              disabled={!onEditTargetDate}
              onPress={onEditTargetDate}
              hitSlop={{ top: rpx(8), bottom: rpx(8), right: rpx(8) }}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                alignSelf: 'flex-start',
              }}
            >
              <Typography.Text
                size={rpx(11)}
                color={LD.mist}
                letterSpacing={rpx(0.5)}
              >
                {dueLabel}
              </Typography.Text>
              {onEditTargetDate ? (
                <Pencil
                  size={rpx(11)}
                  color={LD.mist}
                  style={{ marginLeft: rpx(5) }}
                />
              ) : null}
            </TouchableOpacity>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginTop: rpx(4),
              }}
            >
              <Typography.Text size={rpx(19)} color={LD.ink} medium>
                {formatDate(lead.targetServiceDate)}
              </Typography.Text>
              {urgency ? (
                <View
                  style={{
                    marginLeft: rpx(10),
                    backgroundColor: urgency.bg,
                    borderRadius: rpx(11),
                    paddingHorizontal: rpx(9),
                    paddingVertical: rpx(3),
                  }}
                >
                  <Typography.Text size={rpx(11)} color={urgency.fg} medium>
                    {urgency.text}
                  </Typography.Text>
                </View>
              ) : null}
            </View>
          </View>
          {lead.customerPhone ? (
            <TouchableOpacity
              activeOpacity={0.7}
              onPress={() => Linking.openURL(`tel:${lead.customerPhone}`)}
              style={{
                marginLeft: rpx(10),
                width: rpx(40),
                height: rpx(40),
                borderRadius: rpx(20),
                backgroundColor: LD.brand,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Phone size={rpx(19)} color="#fff" />
            </TouchableOpacity>
          ) : null}
        </View>

        <View style={{ marginTop: rpx(6) }}>
          {/* visit intention (到店意向) — a tappable chip; staff can override the
              signal the system auto-derives from outreach outcomes. */}
          <TouchableOpacity
            activeOpacity={onEditIntention ? 0.6 : 1}
            disabled={!onEditIntention}
            onPress={onEditIntention}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingVertical: rpx(9),
            }}
          >
            <Typography.Text size={rpx(13)} color={LD.steel}>
              到店意向
            </Typography.Text>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <View
                style={{
                  backgroundColor: intention.bg,
                  borderRadius: rpx(8),
                  paddingHorizontal: rpx(10),
                  paddingVertical: rpx(3),
                }}
              >
                <Typography.Text size={rpx(13)} color={intention.fg} medium>
                  {LeadIntentionLabel[lead.intention]}
                </Typography.Text>
              </View>
              {onEditIntention ? (
                <Pencil
                  size={rpx(13)}
                  color={LD.mist}
                  style={{ marginLeft: rpx(6) }}
                />
              ) : null}
            </View>
          </TouchableOpacity>
          {closing ? (
            <Field
              label="结果"
              value={`${closing.summary} · ${formatReachTime(closing.at)}`}
              divider
            />
          ) : lastReach ? (
            <Field
              label="上次跟进"
              value={`${lastReach.summary} · ${formatReachTime(lastReach.at)}`}
              divider
            />
          ) : null}
          <Field
            label="客户信息"
            value={customer}
            divider
            onPress={onEditCustomer}
            emptyText="(暂无)"
          />
          {lead.convertedOrderNo ? (
            <Field label="转化订单" value={lead.convertedOrderNo} />
          ) : null}
          {lead.outcomeReason ? (
            <Field label="结果原因" value={lead.outcomeReason} />
          ) : null}
        </View>
      </View>
    );
  },
);
